import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { cleanObject } from "../../helpers";
import {
  fetchAllActiveBags,
  fetchBuyerRequestDetails,
  fetchBuyersRequests,
  fetchPaidBags,
  fetchRequestsSummary,
  fetchVendorsResponses,
} from "../../apis/requests";

interface RequestState {
  loading: boolean;
  error: string | null;
  requestSummary: any;
  buyersRequests: any;
  activeBags: any;
  paidBags: any;
  selectedActiveBag: any;
  buyerRequestDetails: any;
  vendorResponses: any;
}

const initialState: RequestState = {
  loading: false,
  error: null,
  requestSummary: null,
  buyersRequests: null,
  buyerRequestDetails: null,
  vendorResponses: null,
  activeBags: null,
  paidBags: null,
  selectedActiveBag: null,
};

// Fetch request metrics
export const fetchRequestSummaryAsync = createAsyncThunk(
  "Request/fetchRequestSummary",
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetchRequestsSummary();
      return response?.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message ?? "Failed to fetch requests summary"
      );
    }
  }
);

// Fetch buyers requests
export const fetchBuyersRequestsAsync = createAsyncThunk(
  "Request/fetchBuyersRequests",
  async (params: any, { rejectWithValue }) => {
    try {
      const response = await fetchBuyersRequests(cleanObject(params));
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message ?? "Failed to fetch buyers requests"
      );
    }
  }
);
// Fetch all active bags
export const fetchAllActiveBagsAsync = createAsyncThunk(
  "Request/fetchAllActiveBags",
  async (params: any, { rejectWithValue }) => {
    try {
      const response = await fetchAllActiveBags(cleanObject(params));
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message ?? "Failed to fetch active bags"
      );
    }
  }
);
// Fetch all paid bags
export const fetchPaidBagsAsync = createAsyncThunk(
  "Request/fetchPaidBags`",
  async (params: any, { rejectWithValue }) => {
    try {
      const response = await fetchPaidBags(cleanObject(params));
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message ?? "Failed to fetch paid bags"
      );
    }
  }
);
// Fetch buyer request details
export const fetchBuyerRequestsDetailsAsync = createAsyncThunk(
  "Request/fetchBuyerRequestDetails",
  async (requestId: string | undefined, { rejectWithValue }) => {
    try {
      const response = await fetchBuyerRequestDetails(requestId);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message ?? "Failed to fetch buyer request details"
      );
    }
  }
);
// Fetch vendors responses
export const fetchVendorsResponsesAsync = createAsyncThunk(
  "Request/fetchVendorsResponses",
  async (params: any, { rejectWithValue }) => {
    try {
      const response = await fetchVendorsResponses(cleanObject(params));
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message ?? "Failed to fetch vendors responses"
      );
    }
  }
);

const RequestSlice = createSlice({
  name: "Request",
  initialState,
  reducers: {
    setSelectedActiveBag: (state, action) => {
      state.selectedActiveBag = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch request summary
      .addCase(fetchRequestSummaryAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRequestSummaryAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.requestSummary = action.payload;
      })
      .addCase(
        fetchRequestSummaryAsync.rejected,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload;
        }
      )

      // Fetch buyers requests
      .addCase(fetchBuyersRequestsAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBuyersRequestsAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.buyersRequests = action.payload;
      })
      .addCase(
        fetchBuyersRequestsAsync.rejected,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload;
        }
      )

      // Fetch vendor responses
      .addCase(fetchVendorsResponsesAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchVendorsResponsesAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.vendorResponses = action.payload;
      })
      .addCase(
        fetchVendorsResponsesAsync.rejected,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload;
        }
      )

      // Fetch buyer request details
      .addCase(fetchBuyerRequestsDetailsAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBuyerRequestsDetailsAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.buyerRequestDetails = action.payload;
      })
      .addCase(
        fetchBuyerRequestsDetailsAsync.rejected,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload;
        }
      )
      // Fetch all active bags
      .addCase(fetchAllActiveBagsAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllActiveBagsAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.activeBags = action.payload;
      })
      .addCase(
        fetchAllActiveBagsAsync.rejected,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload;
        }
      )
      // Fetch all paid bags
      .addCase(fetchPaidBagsAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPaidBagsAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.paidBags = action.payload;
      })
      .addCase(
        fetchPaidBagsAsync.rejected,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload;
        }
      );
  },
});

export const { setSelectedActiveBag } = RequestSlice.actions;
export default RequestSlice.reducer;
